import React, { useState } from "react";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Search, Check } from "lucide-react";
import { toast } from "sonner";

export default function VerifyPage() {
  const [phone, setPhone] = useState("");
  const [rows, setRows] = useState([]);
  const [searched, setSearched] = useState(false);
  const [busy, setBusy] = useState(false);

  const search = async () => {
    if (!phone.trim()) return;
    setBusy(true);
    try {
      const r = await api.verifyByPhone(phone.trim());
      setRows(r.data || []);
    } catch (e) {
      toast.error("Search failed");
    } finally {
      setSearched(true);
      setBusy(false);
    }
  };

  const redeem = async (couponId) => {
    try {
      await api.redeemCoupon(couponId);
      toast.success("Coupon redeemed");
      setRows((arr) =>
        arr.map((r) =>
          r.coupon?.id === couponId
            ? { ...r, coupon: { ...r.coupon, is_redeemed: true, redeemed_at: new Date().toISOString() } }
            : r
        )
      );
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to redeem");
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-heading text-3xl font-semibold">Verify coupon</h1>
        <p className="text-muted-foreground text-sm">Look up a customer by phone and redeem their prize.</p>
      </div>

      <Card className="p-5 shadow-[var(--shadow-card)]">
        <div className="flex flex-col md:flex-row md:items-center gap-3">
          <div className="relative md:w-[360px]">
            <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input
              className="pl-9"
              placeholder="Customer phone (e.g. +91...)"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && search()}
              data-testid="verify-phone-input"
            />
          </div>
          <Button onClick={search} disabled={busy} data-testid="verify-search-button">Search</Button>
        </div>
      </Card>

      {searched && (
        <Card className="p-5 shadow-[var(--shadow-card)]">
          {rows.length === 0 ? (
            <p className="text-muted-foreground text-sm text-center py-6" data-testid="verify-empty">
              No wins found for this phone number.
            </p>
          ) : (
            <div className="divide-y divide-border">
              {rows.map((r) => (
                <div key={r.id} className="py-4 flex items-center gap-4" data-testid="verify-row">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-medium">{r.name}</span>
                      <span className="text-xs text-muted-foreground">• {r.campaign_name}</span>
                    </div>
                    <div className="text-sm text-muted-foreground">
                      Won <span className="text-foreground font-medium">{r.won_gift_name || r.won_label}</span> on{" "}
                      {new Date(r.spun_at).toLocaleString()}
                    </div>
                    {r.coupon ? (
                      <div className="mt-1 text-sm font-mono tracking-widest" data-testid="verify-coupon-code">
                        {r.coupon.code}
                      </div>
                    ) : (
                      <div className="mt-1 text-xs text-muted-foreground">No coupon (better luck)</div>
                    )}
                  </div>
                  {r.coupon && (
                    r.coupon.is_redeemed ? (
                      <Badge variant="outline" data-testid="verify-redeemed-badge">
                        <Check className="h-3 w-3 mr-1" /> Redeemed
                      </Badge>
                    ) : (
                      <Button
                        size="sm"
                        onClick={() => redeem(r.coupon.id)}
                        data-testid="verify-redeem-button"
                      >
                        Mark redeemed
                      </Button>
                    )
                  )}
                </div>
              ))}
            </div>
          )}
        </Card>
      )}
    </div>
  );
}
